import React, { useState } from "react"
import AssignmentCard from "../assignments/AssignmentCard"

const AssignmentSection = ({
  title,
  assignments,
  urgency,
  emptyMessage,
  collapsed: defaultCollapsed = false,
  onRefresh,
  onRemoveAssignment,
  onEditAssignment,
  onDeleteAssignment,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  // Only hide section if empty AND no message to display
  if (!emptyMessage && assignments.length === 0) {
    return null
  }

  // Urgency configuration with refined colors
  const urgencyConfig = {
    overdue: {
      border: "border-l-red-500",
      icon: "text-red-500",
      title: "text-red-700",
      bg: "bg-red-50/50",
      iconBg: "bg-red-100",
    },
    today: {
      border: "border-l-amber-500",
      icon: "text-amber-500",
      title: "text-amber-700",
      bg: "bg-amber-50/50",
      iconBg: "bg-amber-100",
    },
    due_soon: {
      border: "border-l-violet-500",
      icon: "text-violet-500",
      title: "text-violet-700",
      bg: "bg-violet-50/50",
      iconBg: "bg-violet-100",
    },
    tomorrow: {
      border: "border-l-orange-400",
      icon: "text-orange-500",
      title: "text-orange-700",
      bg: "bg-orange-50/50",
      iconBg: "bg-orange-100",
    },
    this_week: {
      border: "border-l-blue-500",
      icon: "text-blue-500",
      title: "text-blue-700",
      bg: "bg-blue-50/50",
      iconBg: "bg-blue-100",
    },
    next_week: {
      border: "border-l-indigo-400",
      icon: "text-indigo-400",
      title: "text-indigo-600",
      bg: "bg-indigo-50/50",
      iconBg: "bg-indigo-100",
    },
    later: {
      border: "border-l-[var(--cream)]",
      icon: "text-[var(--ink-light)]",
      title: "text-[var(--ink-muted)]",
      bg: "bg-[var(--paper-warm)]",
      iconBg: "bg-[var(--cream)]",
    },
    none: {
      border: "border-l-[var(--cream)]",
      icon: "text-[var(--ink-light)]",
      title: "text-[var(--ink-light)]",
      bg: "bg-[var(--paper-warm)]",
      iconBg: "bg-[var(--cream)]",
    },
    submitted: {
      border: "border-l-emerald-500",
      icon: "text-emerald-500",
      title: "text-emerald-700",
      bg: "bg-emerald-50/50",
      iconBg: "bg-emerald-100",
    },
    completed: {
      border: "border-l-emerald-500",
      icon: "text-emerald-500",
      title: "text-emerald-700",
      bg: "bg-emerald-50/50",
      iconBg: "bg-emerald-100",
    },
  }

  const config = urgencyConfig[urgency] || urgencyConfig.later

  // Icons for each section
  const sectionIcons = {
    overdue: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    today: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    due_soon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    tomorrow: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    this_week: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    next_week: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    later: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    ),
    none: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    submitted: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    completed: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  }

  return (
    <section className={`rounded-xl overflow-hidden transition-all duration-200 ${collapsed ? "" : config.bg}`}>
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className={`flex items-center gap-3 w-full px-4 py-3 transition-colors ${
          collapsed ? "hover:bg-[var(--cream)]" : ""
        }`}
      >
        {/* Icon */}
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${config.iconBg}`}>
          <span className={config.icon}>
            {sectionIcons[urgency] || sectionIcons.later}
          </span>
        </div>

        {/* Title and count */}
        <div className="flex-1 flex items-center gap-2 text-left">
          <h2 className={`font-semibold ${config.title}`}>
            {title}
          </h2>
          <span className={`
            px-2 py-0.5 rounded-full text-xs font-medium
            ${assignments.length > 0 ? `${config.iconBg} ${config.title}` : "bg-[var(--cream)] text-[var(--ink-light)]"}
          `}>
            {assignments.length}
          </span>
        </div>

        {/* Expand/Collapse indicator */}
        <svg
          className={`w-5 h-5 text-[var(--ink-light)] transition-transform duration-200 ${
            collapsed ? "" : "rotate-180"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Content */}
      {!collapsed && (
        <div className="px-4 pb-4 space-y-3">
          {assignments.length === 0 ? (
            <p className="text-sm text-[var(--ink-light)] py-4 text-center">
              {emptyMessage}
            </p>
          ) : (
            assignments.map((assignment, index) => (
              <div key={assignment.id} className="stagger-in" style={{ animationDelay: `${index * 30}ms` }}>
                <AssignmentCard
                  assignment={assignment}
                  onToggleComplete={onRefresh}
                  onRemove={onRemoveAssignment}
                  onEdit={onEditAssignment}
                  onDelete={onDeleteAssignment}
                />
              </div>
            ))
          )}
        </div>
      )}
    </section>
  )
}

export default AssignmentSection
